import React, { useState, useContext } from 'react';
import { useApiFetch } from '../../utils/useApiFetch';
import { useNavigate, Navigate } from 'react-router-dom';
import { useIsAuthenticated } from "../../utils/msalCompat";
import { supabase } from '../../utils/supabaseClient';
import { getApiBaseUrl } from '../../utils/Config';
import logoImage from '../../images/stacktalent-339X103.png';
import { AlertContext } from '../../Context/AlertContext';
import Alert from '../../utils/GenAlert';
import { TokenContext } from '../../Context/TokenContext';
import { useCandidate } from '../../Context/CandidateContext';

const InfoWizard = () => {
    const isAuthenticated = useIsAuthenticated();
    const apiFetch = useApiFetch();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const { showAlert } = useContext(AlertContext);
    // email is extracted correctly from the B2C token claims in TokenContext
    const { oid, email } = useContext(TokenContext);
    const { apiData, fetching, isLoading, refetchCandidateDetails } = useCandidate();

    // Already registered — don't let them land on this form again
    if (!fetching && !isLoading && apiData) {
        return <Navigate to="/candidate/profile" replace />;
    }

    const handleCandidateSubmit = async (e) => {
        e.preventDefault();
        if (!oid || !email) {
            showAlert('Authentication not ready. Please wait a moment and try again.', false);
            return;
        }
        setIsSubmitting(true);

        const candidateData = {
            FirstName: firstName,
            LastName: lastName,
            Email: email,
            AuthUserID: oid,
        };

        try {
            const baseUrl = getApiBaseUrl();
            const response = await apiFetch(`${baseUrl}/api/Candidate/Register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(candidateData)
            });
            const data = await response.json();

            if (data.success) {
                await supabase.auth.refreshSession(); // pull updated app_metadata (user_type) into the token
                refetchCandidateDetails();
                navigate('/candidate/profile');
                return;
            } else {
                const msg = data.message || '';
                if (msg.toLowerCase().includes('already exists')) {
                    navigate('/candidate/profile');
                    return;
                }
                throw new Error(msg || 'Something went wrong. Please try again.');
            }
        } catch (error) {
            showAlert(error.message || 'Something went wrong. Please try again.', false);
            setIsSubmitting(false);
        }
    };
    if (!isAuthenticated) {
        return <div>Please log in to access the dashboard.</div>;
    }

    return (
        <div>
            <AlertContext.Consumer>
                {({ alert }) => (
                    <>
                        {alert.visible && <Alert message={alert.message} success={alert.success} />}
                    </>
                )}
            </AlertContext.Consumer>
            <div className="px-5 py-5 p-lg-0 bg-surface-secondary d-flex flex-column justify-content-center">
                <div className="d-flex justify-content-center">
                    <div className="col-12 col-md-9 col-lg-6 min-h-lg-screen d-flex flex-column justify-content-center py-lg-16 px-lg-20 position-relative">
                        <div className="row">
                            <div className="col-lg-10 col-md-10 col-xl-10 mx-auto">
                                <div className="text-center mb-12">
                                    <a className="d-inline-block" href="#">
                                        <img src={logoImage} className="h-12" alt="Stacktalent white logo"></img>
                                    </a>
                                    <h1 className="ls-tight font-bolder mt-6">
                                        Almost Done
                                    </h1>
                                    <p className="mt-2">We need some additional information from you</p>
                                </div>
                                <div className="info-wizard">
                                    <form onSubmit={handleCandidateSubmit}>
                                        <h2>Candidate Information</h2>
                                        <div className="mb-5">
                                            <label className="form-label" htmlFor="firstName">First Name:</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={firstName}
                                                onChange={(e) => setFirstName(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="mb-5">
                                            <label className="form-label" htmlFor="lastName">Last Name:</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={lastName}
                                                onChange={(e) => setLastName(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <button type="submit" className="btn btn-primary w-full" disabled={isSubmitting}>
                                            {isSubmitting ? 'Submitting...' : 'Finish'}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InfoWizard;
